import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // Load the initial session and WAIT for the profile before clearing loading.
    // Otherwise the app can paint with loading=false but user=null and bounce a
    // logged-in user to /login on every refresh.
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        await fetchUserProfile(session.user);
      }
      if (mounted) setLoading(false);
    });

    // React to later auth changes (login, logout, token refresh).
    // INITIAL_SESSION is already handled by getSession() above, so skip it to
    // avoid a duplicate, racing profile fetch.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') return;
      if (session) {
        fetchUserProfile(session.user);
      } else {
        setUser(null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const fetchUserProfile = async (authUser) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .single();

      if (error) throw error;

      setUser({
        id: authUser.id,
        email: authUser.email,
        username: data?.username || authUser.email?.split('@')[0],
        role: data?.role || 'operator'
      });
    } catch (error) {
      // Do NOT fabricate a user/role on failure — that would silently grant an
      // 'operator' session, or mis-route an admin whose profile failed to load.
      // Treat a failed/missing profile as "no valid user".
      console.error('Error fetching user profile:', error);
      setUser(null);
    }
  };

  const login = async (username, password) => {
    try {
      // Try to sign in with email (using username as email format)
      const { data, error } = await supabase.auth.signInWithPassword({
        email: `${username}@reshview.com`,
        password: password
      });

      if (error) throw error;

      await fetchUserProfile(data.user);

      return data;
    } catch (error) {
      throw new Error(error.message || 'Invalid username or password');
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
    } catch (error) {
      console.error('Error logging out:', error);
      throw error;
    }
  };

  const value = {
    user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};